import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useCompanyLens } from '../hooks/useCompanyLens';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { toast } from 'sonner';
import { 
  Search, 
  FolderKanban, 
  MapPin, 
  Calendar,
  Loader2,
  Filter,
  ArrowUpDown,
  TrendingUp,
  AlertCircle,
  User,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  HardHat,
  FileText,
  Eye,
  PoundSterling,
  Clock,
  CheckCircle2,
  Pause,
  FolderX
} from 'lucide-react';
import { format } from 'date-fns';

const STATUS_OPTIONS = ['All', 'Live', 'Complete', 'Archive'];
const SORT_OPTIONS = [
  { value: 'created_desc', label: 'Newest First' },
  { value: 'created_asc', label: 'Oldest First' },
  { value: 'number_desc', label: 'Project # (High-Low)' },
  { value: 'number_asc', label: 'Project # (Low-High)' },
  { value: 'status', label: 'Status' }
];

const STATUS_COLORS = {
  'Enquiry': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  'Live': 'bg-green-500/20 text-green-400 border-green-500/30',
  'Complete': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  'Archive': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
};

const ITEMS_PER_PAGE = 10;

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// Hours traffic light indicator component
const HoursIndicator = ({ hoursData }) => {
  if (!hoursData) return <span className="text-gray-500">-</span>;
  
  const { status, percent, hours_allowed, actual_hours } = hoursData;
  
  const colors = {
    green: 'bg-green-500',
    amber: 'bg-amber-500',
    red: 'bg-red-500',
    grey: 'bg-gray-500'
  };
  
  const tooltipText = status === 'grey' 
    ? 'No hours data yet'
    : `${actual_hours}h / ${hours_allowed}h (${percent}%)`;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="flex items-center justify-center">
            <span className={`w-3 h-3 rounded-full ${colors[status] || colors.grey}`}></span>
          </div>
        </TooltipTrigger>
        <TooltipContent className="bg-[#1E2530] border-white/10 text-white">
          <p>{tooltipText}</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Pagination component
const Pagination = ({ currentPage, totalPages, onPageChange, testIdPrefix }) => {
  if (totalPages <= 1) return null;
  
  const pages = [];
  const maxVisiblePages = 5;
  
  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
  
  if (endPage - startPage + 1 < maxVisiblePages) {
    startPage = Math.max(1, endPage - maxVisiblePages + 1);
  }
  
  for (let i = startPage; i <= endPage; i++) {
    pages.push(i);
  }
  
  return (
    <div className="flex items-center justify-end gap-1 mt-4">
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className="h-8 w-8 p-0 text-gray-400 hover:text-white"
        data-testid={`${testIdPrefix}-prev-page`}
      >
        <ChevronLeft className="h-4 w-4" />
      </Button>
      
      {startPage > 1 && (
        <>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(1)}
            className="h-8 w-8 p-0 text-gray-400 hover:text-white"
          >
            1
          </Button>
          {startPage > 2 && <span className="text-gray-500 px-1">...</span>}
        </>
      )}
      
      {pages.map(page => (
        <Button
          key={page}
          variant={currentPage === page ? "default" : "ghost"}
          size="sm"
          onClick={() => onPageChange(page)}
          className={`h-8 w-8 p-0 ${
            currentPage === page 
              ? 'bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]' 
              : 'text-gray-400 hover:text-white'
          }`}
          data-testid={`${testIdPrefix}-page-${page}`}
        >
          {page}
        </Button>
      ))}
      
      {endPage < totalPages && (
        <>
          {endPage < totalPages - 1 && <span className="text-gray-500 px-1">...</span>}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onPageChange(totalPages)}
            className="h-8 w-8 p-0 text-gray-400 hover:text-white"
          >
            {totalPages}
          </Button>
        </>
      )}
      
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className="h-8 w-8 p-0 text-gray-400 hover:text-white"
        data-testid={`${testIdPrefix}-next-page`}
      >
        <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  );
};

// Project table component - enhanced with Project Type, Contract Value and Hours columns
// "Folders not created yet": only when we actually know — skipped on tender win, or a recent project with no folder recorded.
// Older projects predate folder tracking, so a missing flag there proves nothing; their page still has Verify/Repair.
const FOLDER_TRACKING_FROM = '2026-10-24';
const foldersMissing = (p) => p.status === 'Live' && !p.folder_structure_created && (!!p.folders_skipped_at || (!p.folder_path && (p.created_at || '') >= FOLDER_TRACKING_FROM));

const ProjectTable = ({ projects, testIdPrefix, hoursData, showHoursColumn, onDismissFlag, onMarkComplete, onCreateFolders, creatingFoldersId, canCreateFolders }) => {
  if (projects.length === 0) {
    return (
      <div className="py-8 text-center">
        <FolderKanban className="w-10 h-10 mx-auto mb-3 text-gray-600" />
        <p className="text-gray-400">No projects found</p>
      </div>
    );
  }
  
  return (
    <Table className="table-fixed">
      <TableHeader>
        <TableRow className="border-white/10 hover:bg-transparent">
          <TableHead className="text-gray-400 font-medium w-[24%]">Project</TableHead>
          <TableHead className="text-gray-400 font-medium w-[14%]">Client</TableHead>
          <TableHead className="text-gray-400 font-medium hidden lg:table-cell w-[12%]">Location</TableHead>
          <TableHead className="text-gray-400 font-medium w-[8%]">Status</TableHead>
          <TableHead className="text-gray-400 font-medium hidden md:table-cell w-[10%]">Type</TableHead>
          <TableHead className="text-gray-400 font-medium hidden md:table-cell w-[10%] text-right">Contract Value</TableHead>
          {showHoursColumn && (
            <TableHead className="text-gray-400 font-medium hidden lg:table-cell w-[6%] text-center">Hours</TableHead>
          )}
          <TableHead className="text-gray-400 font-medium hidden xl:table-cell w-[10%]">Created</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {projects.map((project, index) => (
          <TableRow 
            key={project.id}
            className="border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
            data-testid={`${testIdPrefix}-row-${index}`}
          >
            <TableCell className="py-3 w-[24%]">
              <Link to={`/projects/${project.id}`} className="block">
                <span 
                  className={`text-base font-bold transition-colors ${
                    project.project_type === 'CON' 
                      ? 'text-sky-400 hover:text-sky-300' 
                      : 'text-[#F5A623] hover:text-[#F5A623]/80'
                  }`}
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  title={project.project_ref}
                >
                  {project.project_ref || `${project.project_type === 'CON' ? 'LC' : 'LUX'}${project.project_number}-${project.project_type || 'DEV'}-${project.address_line1}`}
                </span>
              </Link>
              {canCreateFolders && foldersMissing(project) && (
                <div className="mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`folders-missing-${project.id}`}>
                  <Badge className="bg-amber-500/15 text-amber-300 border-amber-500/30 text-[10px] inline-flex items-center gap-1" title={project.folders_skipped_by ? `Folder creation was skipped by ${project.folders_skipped_by} when the tender was won` : 'No SharePoint folder structure has been created for this project yet'}>
                    <FolderX className="w-3 h-3" /> Folders not created yet
                  </Badge>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onCreateFolders?.(project); }}
                    disabled={creatingFoldersId === project.id}
                    className="text-[10px] text-amber-300 hover:text-white underline disabled:opacity-60 disabled:no-underline inline-flex items-center gap-1"
                    data-testid={`create-folders-${project.id}`}
                  >
                    {creatingFoldersId === project.id ? <><Loader2 className="w-3 h-3 animate-spin" /> Creating…</> : 'Create folders'}
                  </button>
                </div>
              )}
              {project.surveyor_archive_flag?.status === 'pending' && (
                <div className="mt-1 flex items-center gap-1.5 flex-wrap" data-testid={`archive-flag-${project.id}`}>
                  <Badge className="bg-emerald-500/20 text-emerald-300 border-emerald-500/30 text-[10px]" title={`${project.surveyor_archive_flag.requested_by_name || 'Surveyor'} marked this complete${project.surveyor_archive_flag.requested_at ? ` on ${new Date(project.surveyor_archive_flag.requested_at).toLocaleDateString('en-GB')}` : ''}${project.surveyor_archive_flag.note ? ` — ${project.surveyor_archive_flag.note}` : ''}`}>
                    Surveyor marked complete
                  </Badge>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onMarkComplete?.(project.id); }}
                    className="text-[10px] text-emerald-300 hover:text-white underline"
                    data-testid={`mark-complete-${project.id}`}
                  >
                    Mark complete
                  </button>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDismissFlag?.(project.id); }}
                    className="text-[10px] text-gray-400 hover:text-white underline"
                    data-testid={`dismiss-archive-flag-${project.id}`}
                  >
                    Keep live
                  </button>
                </div>
              )}
            </TableCell>
            <TableCell className="w-[14%]">
              <Link to={`/projects/${project.id}`} className="block">
                <div className="flex items-center gap-2">
                  <User className="w-3.5 h-3.5 text-gray-500 flex-shrink-0" />
                  <p className="text-white font-medium truncate max-w-[100px]" title={project.client_name}>
                    {project.client_name || '-'}
                  </p>
                </div>
              </Link>
            </TableCell>
            <TableCell className="hidden lg:table-cell w-[12%]">
              <Link to={`/projects/${project.id}`} className="block">
                <div className="flex items-center text-sm text-gray-400">
                  <MapPin className="w-3.5 h-3.5 mr-1.5 flex-shrink-0" />
                  <span className="truncate max-w-[100px]">{project.town_city}, {project.postcode}</span>
                </div>
              </Link>
            </TableCell>
            <TableCell className="w-[8%]">
              <Link to={`/projects/${project.id}`} className="block">
                <Badge className={`${STATUS_COLORS[project.status]} text-xs`}>
                  {project.status}
                </Badge>
              </Link>
            </TableCell>
            <TableCell className="hidden md:table-cell w-[10%]">
              <Link to={`/projects/${project.id}`} className="block">
                <span className="text-gray-400 text-sm truncate max-w-[90px] block">
                  {project.primary_project_type || '-'}
                </span>
              </Link>
            </TableCell>
            <TableCell className="hidden md:table-cell w-[10%] text-right">
              <Link to={`/projects/${project.id}`} className="block">
                <span className={`font-medium ${project.contract_value ? 'text-[#F5A623]' : 'text-gray-500'}`}>
                  {project.contract_value ? formatCurrency(project.contract_value) : '-'}
                </span>
              </Link>
            </TableCell>
            {showHoursColumn && (
              <TableCell className="hidden lg:table-cell w-[6%]">
                <Link to={`/projects/${project.id}`} className="block">
                  <HoursIndicator hoursData={hoursData?.[project.id]} />
                </Link>
              </TableCell>
            )}
            <TableCell className="hidden xl:table-cell w-[10%]">
              <Link to={`/projects/${project.id}`} className="block">
                <div className="flex items-center text-xs text-gray-500">
                  <Calendar className="w-3 h-3 mr-1" />
                  {format(new Date(project.created_at), 'dd MMM yyyy')}
                </div>
              </Link>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// Collapsible KPI group header (chevron + label). Collapse state lives in the parent.
const KpiGroupHeader = ({ label, collapsed, onToggle, testid }) => (
  <button
    type="button"
    onClick={onToggle}
    data-testid={testid}
    className="flex items-center gap-1.5 mb-2 text-xs text-gray-500 uppercase font-medium hover:text-gray-300 transition-colors"
  >
    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${collapsed ? '-rotate-90' : ''}`} />
    {label}
  </button>
);

// Project Type Breakdown component (Change 3)
const ProjectTypeBreakdown = ({ breakdown, accentColor, testIdPrefix, collapsed, onToggle }) => {
  if (!breakdown || breakdown.length === 0) return null;
  
  // Filter to only show types with data
  const activeTypes = breakdown.filter(pt => pt.live_count > 0 || pt.pipeline_count > 0);
  if (activeTypes.length === 0) return null;
  
  const isOrange = accentColor === 'orange';
  
  // Check if there are any live projects (affects the label text)
  const hasLiveProjects = activeTypes.some(pt => pt.live_count > 0);
  
  return (
    <Card className="bg-[#2A3240] border-white/10 mt-4">
      <CardContent className="p-4">
        <button
          type="button"
          onClick={onToggle}
          data-testid={`${testIdPrefix}-kpi-breakdown-toggle`}
          className="flex items-center gap-1.5 text-xs text-gray-500 uppercase font-medium hover:text-gray-300 transition-colors"
        >
          <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${collapsed ? '-rotate-90' : ''}`} />
          Project Type Breakdown
        </button>
        {!collapsed && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-4">
          {activeTypes.map((pt) => (
            <div 
              key={pt.project_type} 
              className="p-3 rounded-lg bg-[#1E2530] border border-white/10"
              data-testid={`${testIdPrefix}-type-${pt.project_type.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <p className="text-sm text-white font-medium mb-2">{pt.project_type}</p>
              
              {/* Headline percentage */}
              <p className={`text-2xl font-bold ${isOrange ? 'text-[#F5A623]' : 'text-sky-400'}`}>
                {pt.share_of_live_workload}%
              </p>
              <p className="text-xs text-gray-500 mb-3">
                {hasLiveProjects ? 'of live workload' : 'of pipeline value'}
              </p>
              
              {/* Live and Pipeline counts */}
              <div className="flex gap-3 mb-2">
                <div>
                  <p className="text-xs text-gray-500">Live</p>
                  <p className="text-sm text-green-400 font-medium">{pt.live_count}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Pipeline</p>
                  <p className="text-sm text-amber-400 font-medium">{pt.pipeline_count}</p>
                </div>
              </div>
              
              {/* Values */}
              <div className="space-y-1 pt-2 border-t border-white/10">
                <p className="text-xs">
                  <span className="text-gray-500">Live:</span>{' '}
                  <span className="text-green-400">{pt.live_value > 0 ? formatCurrency(pt.live_value) : '—'}</span>
                </p>
                <p className="text-xs">
                  <span className="text-gray-500">Pipeline:</span>{' '}
                  <span className="text-amber-400">{pt.pipeline_value > 0 ? formatCurrency(pt.pipeline_value) : '—'}</span>
                </p>
                <p className="text-xs">
                  <span className="text-gray-500">% of turnover:</span>{' '}
                  <span className="text-white">{pt.share_of_total_turnover}%</span>
                </p>
                <p className="text-xs">
                  <span className="text-gray-500">Avg value:</span>{' '}
                  <span className="text-white">{pt.avg_contract_value > 0 ? formatCurrency(pt.avg_contract_value) : '—'}</span>
                </p>
              </div>
            </div>
          ))}
        </div>
        )}
      </CardContent>
    </Card>
  );
};

// Metrics component - enhanced with summary cards and status values
const MetricsSection = ({ 
  dashboard,
  loading, 
  accentColor,
  onStatusFilter,
  activeStatusFilter,
  invoices,
  loadingInvoices,
  showInvoices,
  onToggleInvoices,
  testIdPrefix,
  collapse,
  onToggle
}) => {
  const isOrange = accentColor === 'orange';
  
  const INVOICE_STATUS_COLORS = {
    'draft': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
    'sent': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'paid': 'bg-green-500/20 text-green-400 border-green-500/30',
    'overdue': 'bg-red-500/20 text-red-400 border-red-500/30',
    'cancelled': 'bg-gray-500/20 text-gray-400 border-gray-500/30'
  };
  
  return (
    <div className="space-y-4">
      {/* Row 1: Contract Position - 2 cards */}
      <div>
        <KpiGroupHeader label="Contract Position" collapsed={collapse.contract} onToggle={() => onToggle('contract')} testid={`${testIdPrefix}-kpi-contract-toggle`} />
        {!collapse.contract && (
        <div className="grid grid-cols-2 gap-3">
          {/* Live Contract Value */}
          <Card className="bg-gradient-to-br from-[#2A3240] to-[#1E2530] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-1.5 rounded-lg ${isOrange ? 'bg-[#F5A623]/20' : 'bg-sky-500/20'}`}>
                  <PoundSterling className={`w-4 h-4 ${isOrange ? 'text-[#F5A623]' : 'text-sky-400'}`} />
                </div>
                <span className="text-gray-400 text-xs font-medium">Live Contract Value</span>
              </div>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
              ) : (
                <p 
                  className={`text-xl font-bold ${isOrange ? 'text-[#F5A623]' : 'text-sky-400'}`}
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  data-testid={`${testIdPrefix}-live-value`}
                >
                  {formatCurrency(dashboard?.live_contract_value)}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Won - Not Started */}
          <Card className="bg-gradient-to-br from-[#2A3240] to-[#1E2530] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-amber-500/20">
                  <Pause className="w-4 h-4 text-amber-400" />
                </div>
                <span className="text-gray-400 text-xs font-medium">Won - Not Started</span>
              </div>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
              ) : (
                <p 
                  className="text-xl font-bold text-amber-400"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  data-testid={`${testIdPrefix}-won-not-started`}
                >
                  {formatCurrency(dashboard?.won_not_started_value)}
                </p>
              )}
            </CardContent>
          </Card>
        </div>
        )}
      </div>

      {/* Row 2: Cash Position - 5 cards */}
      <div>
        <KpiGroupHeader label="Cash Position" collapsed={collapse.cash} onToggle={() => onToggle('cash')} testid={`${testIdPrefix}-kpi-cash-toggle`} />
        {!collapse.cash && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {/* Total Billed */}
          <Card className="bg-gradient-to-br from-[#2A3240] to-[#1E2530] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-blue-500/20">
                  <FileText className="w-4 h-4 text-blue-400" />
                </div>
                <span className="text-gray-400 text-xs font-medium">Total Billed</span>
              </div>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
              ) : (
                <p 
                  className="text-xl font-bold text-white"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  data-testid={`${testIdPrefix}-total-billed`}
                >
                  {formatCurrency(dashboard?.total_billed)}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Total Paid */}
          <Card className="bg-gradient-to-br from-[#2A3240] to-[#1E2530] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-green-500/20">
                  <CheckCircle2 className="w-4 h-4 text-green-400" />
                </div>
                <span className="text-gray-400 text-xs font-medium">Total Paid</span>
              </div>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
              ) : (
                <p 
                  className="text-xl font-bold text-green-400"
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  data-testid={`${testIdPrefix}-total-paid`}
                >
                  {formatCurrency(dashboard?.total_paid)}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Total Due */}
          <Card className="bg-gradient-to-br from-[#2A3240] to-[#1E2530] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className="p-1.5 rounded-lg bg-amber-500/20">
                  <Clock className="w-4 h-4 text-amber-400" />
                </div>
                <span className="text-gray-400 text-xs font-medium">Total Due</span>
              </div>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
              ) : (
                <p 
                  className={`text-xl font-bold ${(dashboard?.total_due || 0) > 0 ? 'text-amber-400' : 'text-white'}`}
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  data-testid={`${testIdPrefix}-total-due`}
                >
                  {formatCurrency(dashboard?.total_due)}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Works Outstanding */}
          <Card className="bg-gradient-to-br from-[#2A3240] to-[#1E2530] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-1.5 rounded-lg ${isOrange ? 'bg-[#F5A623]/20' : 'bg-sky-500/20'}`}>
                  <TrendingUp className={`w-4 h-4 ${isOrange ? 'text-[#F5A623]' : 'text-sky-400'}`} />
                </div>
                <span className="text-gray-400 text-xs font-medium">Works Outstanding</span>
              </div>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
              ) : (
                <p 
                  className={`text-xl font-bold ${(dashboard?.works_outstanding || 0) < 0 ? 'text-red-400' : 'text-white'}`}
                  style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                  data-testid={`${testIdPrefix}-works-outstanding`}
                >
                  {formatCurrency(dashboard?.works_outstanding)}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Overdue */}
          <Card className="bg-gradient-to-br from-[#2A3240] to-[#1E2530] border-white/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 rounded-lg bg-red-500/20">
                    <AlertCircle className="w-4 h-4 text-red-400" />
                  </div>
                  <span className="text-gray-400 text-xs font-medium">Overdue</span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onToggleInvoices}
                  className={`h-6 px-2 text-[10px] ${
                    isOrange 
                      ? 'text-[#F5A623] hover:bg-[#F5A623]/20' 
                      : 'text-sky-400 hover:bg-sky-400/20'
                  }`}
                  data-testid={`${testIdPrefix}-view-invoices-btn`}
                >
                  <Eye className="w-3 h-3 mr-1" />
                  {showInvoices ? 'Hide' : 'View'}
                </Button>
              </div>
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
              ) : (
                <div>
                  <p 
                    className={`text-xl font-bold ${(dashboard?.overdue_value || 0) > 0 ? 'text-red-400' : 'text-white'}`}
                    style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
                    data-testid={`${testIdPrefix}-overdue-value`}
                  >
                    {formatCurrency(dashboard?.overdue_value)}
                  </p>
                  {(dashboard?.overdue_count || 0) > 0 && (
                    <p className="text-xs text-red-400 mt-0.5">
                      {dashboard.overdue_count} invoice{dashboard.overdue_count !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        )}
      </div>

      {/* Invoices List - Expandable */}
      {!collapse.cash && showInvoices && (
        <Card className="bg-[#2A3240] border-white/10 overflow-hidden">
          <CardContent className="p-0">
            <div className={`px-4 py-3 border-b border-white/10 flex items-center gap-2 ${
              isOrange ? 'bg-[#F5A623]/10' : 'bg-sky-500/10'
            }`}>
              <FileText className={`w-4 h-4 ${isOrange ? 'text-[#F5A623]' : 'text-sky-400'}`} />
              <span className="font-medium text-white">All Invoices</span>
              <Badge className={`ml-auto ${isOrange ? 'bg-[#F5A623]/20 text-[#F5A623]' : 'bg-sky-500/20 text-sky-400'}`}>
                {invoices?.length || 0}
              </Badge>
            </div>
            
            {loadingInvoices ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-500" />
              </div>
            ) : invoices?.length === 0 ? (
              <div className="py-8 text-center">
                <FileText className="w-10 h-10 mx-auto mb-3 text-gray-600" />
                <p className="text-gray-400">No invoices found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-gray-400 font-medium">Invoice #</TableHead>
                    <TableHead className="text-gray-400 font-medium">Project</TableHead>
                    <TableHead className="text-gray-400 font-medium hidden md:table-cell">Client</TableHead>
                    <TableHead className="text-gray-400 font-medium hidden lg:table-cell">Date</TableHead>
                    <TableHead className="text-gray-400 font-medium text-right">Amount</TableHead>
                    <TableHead className="text-gray-400 font-medium">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices?.map((invoice, index) => (
                    <TableRow 
                      key={invoice.id}
                      className="border-white/5 hover:bg-white/5 cursor-pointer transition-colors"
                      data-testid={`${testIdPrefix}-invoice-row-${index}`}
                    >
                      <TableCell className="py-2.5">
                        <Link 
                          to={`/projects/${invoice.project_id}`}
                          className={`font-medium ${isOrange ? 'text-[#F5A623] hover:text-[#F5A623]/80' : 'text-sky-400 hover:text-sky-300'}`}
                        >
                          {invoice.invoice_number}
                        </Link>
                      </TableCell>
                      <TableCell className="py-2.5">
                        <Link 
                          to={`/projects/${invoice.project_id}`}
                          className="text-white hover:text-gray-300 truncate max-w-[150px] block"
                        >
                          {invoice.project_ref || '-'}
                        </Link>
                      </TableCell>
                      <TableCell className="py-2.5 hidden md:table-cell">
                        <span className="text-gray-400 truncate max-w-[120px] block">
                          {invoice.client_name}
                        </span>
                      </TableCell>
                      <TableCell className="py-2.5 hidden lg:table-cell">
                        <span className="text-gray-400 text-sm">
                          {invoice.invoice_date ? format(new Date(invoice.invoice_date), 'dd MMM yyyy') : '-'}
                        </span>
                      </TableCell>
                      <TableCell className="py-2.5 text-right">
                        <span className="text-white font-medium">
                          {formatCurrency(invoice.total)}
                        </span>
                      </TableCell>
                      <TableCell className="py-2.5">
                        <Badge className={`text-xs capitalize ${INVOICE_STATUS_COLORS[invoice.status] || INVOICE_STATUS_COLORS['draft']}`}>
                          {invoice.status}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Status Summary Cards - with contract values (Change 2) */}
      <div className="grid grid-cols-3 gap-3">
        {['Live', 'Complete', 'Archive'].map(status => {
          const countKey = `${status.toLowerCase()}_count`;
          const valueKey = `${status.toLowerCase()}_value`;
          const count = dashboard?.[countKey] || 0;
          const value = dashboard?.[valueKey] || 0;
          
          return (
            <div
              key={status}
              onClick={() => onStatusFilter(activeStatusFilter === status ? 'All' : status)}
              className={`p-3 rounded-lg border cursor-pointer transition-all ${
                activeStatusFilter === status 
                  ? `${isOrange ? 'border-[#F5A623] bg-[#F5A623]/10' : 'border-sky-400 bg-sky-400/10'}` 
                  : 'border-white/10 bg-[#2A3240] hover:border-white/20'
              }`}
              data-testid={`${testIdPrefix}-status-${status.toLowerCase()}`}
            >
              <p className="text-xs text-gray-500 mb-1">{status}</p>
              <p className="text-2xl font-bold text-white">{count}</p>
              {value > 0 && (
                <p className={`text-xs mt-1 ${isOrange ? 'text-[#F5A623]' : 'text-sky-400'}`}>
                  {formatCurrency(value)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export function ProjectsList() {
  const [projects, setProjects] = useState([]);
  const [devDashboard, setDevDashboard] = useState(null);
  const [conDashboard, setConDashboard] = useState(null);
  const [hoursData, setHoursData] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [loadingHours, setLoadingHours] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [sortBy, setSortBy] = useState('number_desc');
  const [devPage, setDevPage] = useState(1);
  const [conPage, setConPage] = useState(1);
  // KPI + section collapse — all persisted. KPI groups start collapsed for a clean first view.
  const [sectionCollapse, setSectionCollapse] = useState(() => {
    const def = { dev: false, con: false };
    try { const s = JSON.parse(localStorage.getItem('lux.projects.sectionCollapse')); return (s && typeof s.dev === 'boolean') ? s : def; } catch { return def; }
  });
  useEffect(() => { try { localStorage.setItem('lux.projects.sectionCollapse', JSON.stringify(sectionCollapse)); } catch { /* ignore */ } }, [sectionCollapse]);
  const [kpiCollapse, setKpiCollapse] = useState(() => {
    const def = { dev: { contract: true, cash: true, breakdown: true }, con: { contract: true, cash: true, breakdown: true } };
    try { const s = JSON.parse(localStorage.getItem('lux.projects.kpiCollapse')); return (s && s.dev && s.con) ? s : def; } catch { return def; }
  });
  useEffect(() => { try { localStorage.setItem('lux.projects.kpiCollapse', JSON.stringify(kpiCollapse)); } catch { /* ignore */ } }, [kpiCollapse]);
  const toggleKpi = (co, group) => setKpiCollapse(s => ({ ...s, [co]: { ...s[co], [group]: !s[co][group] } }));
  const setAllKpi = (co, collapsed) => setKpiCollapse(s => ({ ...s, [co]: { contract: collapsed, cash: collapsed, breakdown: collapsed } }));
  const allKpiCollapsed = (co) => ['contract', 'cash', 'breakdown'].every(g => kpiCollapse[co][g]);
  const toggleSection = (co) => setSectionCollapse(s => ({ ...s, [co]: !s[co] }));
  // Company lens — shared with the Cost Ledger & Inbox (one memory across the app).
  const [companyView, setCompanyView] = useCompanyLens();
  
  // Invoice viewing state
  const [showDevInvoices, setShowDevInvoices] = useState(false);
  const [showConInvoices, setShowConInvoices] = useState(false);
  const [devInvoices, setDevInvoices] = useState([]);
  const [conInvoices, setConInvoices] = useState([]);
  const [loadingDevInvoices, setLoadingDevInvoices] = useState(false);
  const [loadingConInvoices, setLoadingConInvoices] = useState(false);
  
  const { api, isAdmin } = useAuth();
  const [creatingFoldersId, setCreatingFoldersId] = useState(null);

  // One-click SharePoint folder structure for a Live project that was created without one.
  const handleCreateFolders = async (project) => {
    setCreatingFoldersId(project.id);
    try {
      const { data } = await api.post(`/projects/${project.id}/create-folder-structure`);
      if (data?.skipped) {
        toast.info(data.message || 'OneDrive is disabled on this environment — folders were not created');
        return;
      }
      setProjects(prev => prev.map(p => p.id === project.id ? { ...p, folder_structure_created: true, folder_path: data.details?.folder_path || p.folder_path } : p));
      toast.success(`Folders ready for ${project.project_ref}`, { description: `${data.folders_created ?? 0} created, ${data.folders_existed ?? 0} already existed${data.errors?.length ? ` · ${data.errors.length} error${data.errors.length === 1 ? '' : 's'}` : ''}` });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create the folder structure');
    } finally {
      setCreatingFoldersId(null);
    }
  };

  // Load projects
  useEffect(() => {
    let isMounted = true;
    
    const loadProjects = async () => {
      try {
        const params = new URLSearchParams();
        if (search) params.append('search', search);
        if (statusFilter && statusFilter !== 'All') params.append('status', statusFilter);
        
        const response = await api.get(`/projects?${params.toString()}`);
        if (isMounted) {
          // Enhance projects with contract value and primary_project_type from finance
          const projectsWithValues = response.data.map(p => ({
            ...p,
            contract_value: p.finance?.contract_value || 0,
            primary_project_type: p.primary_project_type || null
          }));
          setProjects(projectsWithValues);
        }
      } catch (error) {
        console.error('Failed to fetch projects:', error);
        if (isMounted) toast.error('Failed to load projects');
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    
    loadProjects();
    return () => { isMounted = false; };
  }, [search, statusFilter, api]);

  // Load dashboard summaries
  useEffect(() => {
    let isMounted = true;
    
    const loadDashboards = async () => {
      setLoadingDashboard(true);
      try {
        const [devRes, conRes] = await Promise.all([
          api.get('/projects/dashboard?project_type=DEV').catch(() => ({ data: {} })),
          api.get('/projects/dashboard?project_type=CON').catch(() => ({ data: {} }))
        ]);
        if (isMounted) {
          setDevDashboard(devRes.data);
          setConDashboard(conRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch dashboards:', error);
      } finally {
        if (isMounted) setLoadingDashboard(false);
      }
    };
    
    loadDashboards();
    return () => { isMounted = false; };
  }, [api]);

  // Load hours summary for traffic light indicators
  useEffect(() => {
    let isMounted = true;
    
    const loadHoursData = async () => {
      setLoadingHours(true);
      try {
        const response = await api.get('/projects/hours-summary');
        if (isMounted) setHoursData(response.data || {});
      } catch (error) {
        console.error('Failed to fetch hours summary:', error);
      } finally {
        if (isMounted) setLoadingHours(false);
      }
    };
    
    loadHoursData();
    return () => { isMounted = false; };
  }, [api]);

  // Reset pagination when filters change
  useEffect(() => {
    setDevPage(1);
    setConPage(1);
  }, [search, statusFilter, sortBy]);

  // Fetch invoices for DEV projects
  const fetchDevInvoices = async () => {
    setLoadingDevInvoices(true);
    try {
      const response = await api.get('/invoices?project_type=DEV');
      setDevInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch DEV invoices:', error);
      toast.error('Failed to load Development invoices');
    } finally {
      setLoadingDevInvoices(false);
    }
  };

  // Fetch invoices for CON projects
  const fetchConInvoices = async () => {
    setLoadingConInvoices(true);
    try {
      const response = await api.get('/invoices?project_type=CON');
      setConInvoices(response.data);
    } catch (error) {
      console.error('Failed to fetch CON invoices:', error);
      toast.error('Failed to load Construction invoices');
    } finally {
      setLoadingConInvoices(false);
    }
  };

  // Toggle handlers for invoice visibility
  const handleToggleDevInvoices = () => {
    if (!showDevInvoices && devInvoices.length === 0) {
      fetchDevInvoices();
    }
    setShowDevInvoices(!showDevInvoices);
  };

  const handleToggleConInvoices = () => {
    if (!showConInvoices && conInvoices.length === 0) {
      fetchConInvoices();
    }
    setShowConInvoices(!showConInvoices);
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
  };

  // Admin accepts a surveyor's "marked complete" request → project status Complete.
  const handleMarkComplete = async (projectId) => {
    try {
      await api.put(`/projects/${projectId}`, { status: 'Complete' });
      setProjects(prev => prev.map(p => p.id === projectId
        ? { ...p, status: 'Complete', surveyor_archive_flag: { ...(p.surveyor_archive_flag || {}), status: 'dismissed' } }
        : p));
      toast.success('Project marked Complete — it has left the surveyor\'s diary');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to mark complete');
    }
  };

  // Admin keeps the project live (dismisses the surveyor's prompt).
  const handleDismissArchiveFlag = async (projectId) => {
    try {
      await api.post(`/projects/${projectId}/surveyor-archive/dismiss`);
      setProjects(prev => prev.map(p => p.id === projectId
        ? { ...p, surveyor_archive_flag: { ...(p.surveyor_archive_flag || {}), status: 'dismissed' } }
        : p));
      toast.success('Kept live — the surveyor will see it back in their jobs');
    } catch (e) {
      toast.error('Failed to dismiss prompt');
    }
  };

  // Sort projects based on selected option
  const sortProjects = (projectList) => {
    return [...projectList].sort((a, b) => {
      switch (sortBy) {
        case 'created_desc':
          return new Date(b.created_at) - new Date(a.created_at);
        case 'created_asc':
          return new Date(a.created_at) - new Date(b.created_at);
        case 'number_desc':
          return b.project_number - a.project_number;
        case 'number_asc':
          return a.project_number - b.project_number;
        case 'status':
          return a.status.localeCompare(b.status);
        default:
          return 0;
      }
    });
  };

  // Separate and sort projects by type
  const devProjects = sortProjects(projects.filter(p => p.project_type !== 'CON'));
  const conProjects = sortProjects(projects.filter(p => p.project_type === 'CON'));

  // Paginated projects
  const devTotalPages = Math.ceil(devProjects.length / ITEMS_PER_PAGE);
  const conTotalPages = Math.ceil(conProjects.length / ITEMS_PER_PAGE);
  
  const paginatedDevProjects = devProjects.slice(
    (devPage - 1) * ITEMS_PER_PAGE,
    devPage * ITEMS_PER_PAGE
  );
  const paginatedConProjects = conProjects.slice(
    (conPage - 1) * ITEMS_PER_PAGE,
    conPage * ITEMS_PER_PAGE
  );

  // Check if hours column should be shown
  const hasAnyHoursData = Object.values(hoursData).some(h => h.status !== 'grey');

  const showDev = companyView !== 'CON';
  const showCon = companyView !== 'DEV';
  const visibleCount = companyView === 'DEV' ? devProjects.length
    : companyView === 'CON' ? conProjects.length
    : projects.length;

  const COMPANY_LENS = [
    { key: 'all', label: 'Global', hint: 'Every job across both companies', active: 'bg-[#F5A623] text-[#1E2530] border-[#F5A623]' },
    { key: 'DEV', label: 'LUX Developments', hint: 'LUX Developments jobs only', active: 'bg-[#F5A623] text-[#1E2530] border-[#F5A623]' },
    { key: 'CON', label: 'LUX Construction', hint: 'LUX Construction jobs only', active: 'bg-sky-500 text-white border-sky-500' },
  ];

  return (
    <div className="container mx-auto px-4 py-6 md:py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-6">
        <h1 
          className="text-3xl md:text-4xl font-bold text-white"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
        >
          Projects
        </h1>
        <p className="text-gray-400 mt-1">
          {visibleCount} project{visibleCount !== 1 ? 's' : ''} found
        </p>
      </div>

      {/* Company lens — Global vs each trading company (mirrors the Cost Ledger) */}
      <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="projects-company-view">
        <span className="text-xs uppercase tracking-wider text-gray-500 mr-1">Viewing</span>
        {COMPANY_LENS.map(opt => (
          <button
            key={opt.key}
            type="button"
            onClick={() => setCompanyView(opt.key)}
            title={opt.hint}
            className={`h-9 px-4 rounded-md text-sm border transition-colors ${companyView === opt.key ? `${opt.active} font-semibold` : 'bg-[#2A3240] text-gray-300 border-white/15 hover:bg-white/5'}`}
            data-testid={`projects-company-${opt.key}`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {/* Search, Filter, and Sort */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <Input
            type="text"
            placeholder="Search by reference, address, postcode, or client..."
            value={search}
            onChange={handleSearchChange}
            data-testid="projects-search-input"
            className="pl-10 bg-[#1E2530] border-white/20 text-white placeholder:text-gray-500 focus:border-[#F5A623]"
          />
        </div>
        
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger 
            className="w-full sm:w-[160px] bg-[#1E2530] border-white/20 text-white"
            data-testid="status-filter-select"
          >
            <Filter className="w-4 h-4 mr-2 text-gray-500" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            {STATUS_OPTIONS.map(status => (
              <SelectItem 
                key={status} 
                value={status}
                className="text-white focus:bg-[#F5A623]/20 focus:text-white"
              >
                {status}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger 
            className="w-full sm:w-[180px] bg-[#1E2530] border-white/20 text-white"
            data-testid="sort-select"
          >
            <ArrowUpDown className="w-4 h-4 mr-2 text-gray-500" />
            <SelectValue placeholder="Sort by" />
          </SelectTrigger>
          <SelectContent className="bg-[#2A3240] border-white/10">
            {SORT_OPTIONS.map(option => (
              <SelectItem 
                key={option.value} 
                value={option.value}
                className="text-white focus:bg-[#F5A623]/20 focus:text-white"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Loading State */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-[#F5A623]" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* ============ LUX DEVELOPMENTS SECTION ============ */}
          {showDev && (
          <div>
            {/* Section Header - Clickable to collapse */}
            <div 
              className="flex items-center gap-3 mb-4 cursor-pointer group"
              onClick={() => toggleSection('dev')}
              data-testid="dev-section-toggle"
            >
              <div className="p-2 rounded-lg bg-[#F5A623]/20">
                <HardHat className="w-6 h-6 text-[#F5A623]" />
              </div>
              <h2 
                className="text-2xl font-bold text-white"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                LUX Developments
              </h2>
              <Badge className="bg-[#F5A623]/20 text-[#F5A623] border-[#F5A623]/30 text-xs">
                {devProjects.length} project{devProjects.length !== 1 ? 's' : ''}
              </Badge>
              <ChevronDown 
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                  sectionCollapse.dev ? '-rotate-90' : ''
                }`}
              />
              {!sectionCollapse.dev && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setAllKpi('dev', !allKpiCollapsed('dev')); }}
                  className="ml-auto text-xs px-3 py-1.5 rounded-md border border-white/15 bg-[#2A3240] text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                  data-testid="dev-kpi-collapse-all"
                >
                  {allKpiCollapsed('dev') ? 'Show KPIs' : 'Hide KPIs'}
                </button>
              )}
            </div>

            {!sectionCollapse.dev && (
              <>
                {/* DEV Metrics */}
                <MetricsSection 
                  dashboard={devDashboard}
                  loading={loadingDashboard}
                  accentColor="orange"
                  onStatusFilter={setStatusFilter}
                  activeStatusFilter={statusFilter}
                  invoices={devInvoices}
                  loadingInvoices={loadingDevInvoices}
                  showInvoices={showDevInvoices}
                  onToggleInvoices={handleToggleDevInvoices}
                  testIdPrefix="dev"
                  collapse={kpiCollapse.dev}
                  onToggle={(g) => toggleKpi('dev', g)}
                />

                {/* DEV Project Type Breakdown (Change 3) */}
                <ProjectTypeBreakdown 
                  breakdown={devDashboard?.project_type_breakdown}
                  accentColor="orange"
                  testIdPrefix="dev"
                  collapsed={kpiCollapse.dev.breakdown}
                  onToggle={() => toggleKpi('dev', 'breakdown')}
                />

                {/* DEV Projects Table */}
                <Card className="bg-[#2A3240] border-white/10 overflow-hidden mt-4">
                  <CardContent className="p-0">
                    <ProjectTable 
                      projects={paginatedDevProjects} 
                      testIdPrefix="dev-project"
                      hoursData={hoursData}
                      showHoursColumn={hasAnyHoursData}
                      onDismissFlag={handleDismissArchiveFlag}
                      onMarkComplete={handleMarkComplete}
                      onCreateFolders={handleCreateFolders}
                      creatingFoldersId={creatingFoldersId}
                      canCreateFolders={isAdmin}
                    />
                    <div className="px-4 pb-3">
                      <Pagination 
                        currentPage={devPage} 
                        totalPages={devTotalPages} 
                        onPageChange={setDevPage}
                        testIdPrefix="dev"
                      />
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
          )}

          {/* ============ DIVIDER ============ */}
          {showDev && showCon && (
          <div className="relative my-10">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t-2 border-white/20"></div>
            </div>
          </div>
          )}

          {/* ============ LUX CONSTRUCTION SECTION ============ */}
          {showCon && (
          <div>
            {/* Section Header - Clickable to collapse */}
            <div 
              className="flex items-center gap-3 mb-4 cursor-pointer group"
              onClick={() => toggleSection('con')}
              data-testid="con-section-toggle"
            >
              <div className="p-2 rounded-lg bg-sky-500/20">
                <HardHat className="w-6 h-6 text-sky-400" />
              </div>
              <h2 
                className="text-2xl font-bold text-white"
                style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
              >
                LUX Construction
              </h2>
              <Badge className="bg-sky-500/20 text-sky-400 border-sky-500/30 text-xs">
                {conProjects.length} project{conProjects.length !== 1 ? 's' : ''}
              </Badge>
              <ChevronDown 
                className={`w-5 h-5 text-gray-400 transition-transform duration-200 ${
                  sectionCollapse.con ? '-rotate-90' : ''
                }`}
              />
              {!sectionCollapse.con && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setAllKpi('con', !allKpiCollapsed('con')); }}
                  className="ml-auto text-xs px-3 py-1.5 rounded-md border border-white/15 bg-[#2A3240] text-gray-300 hover:bg-white/5 hover:text-white transition-colors"
                  data-testid="con-kpi-collapse-all"
                >
                  {allKpiCollapsed('con') ? 'Show KPIs' : 'Hide KPIs'}
                </button>
              )}
            </div>

            {!sectionCollapse.con && (
              <>
                {/* CON Metrics */}
                <MetricsSection 
                  dashboard={conDashboard}
                  loading={loadingDashboard}
                  accentColor="blue"
                  onStatusFilter={setStatusFilter}
                  activeStatusFilter={statusFilter}
                  invoices={conInvoices}
                  loadingInvoices={loadingConInvoices}
                  showInvoices={showConInvoices}
                  onToggleInvoices={handleToggleConInvoices}
                  testIdPrefix="con"
                  collapse={kpiCollapse.con}
                  onToggle={(g) => toggleKpi('con', g)}
                />

                {/* CON Project Type Breakdown (Change 3) */}
                <ProjectTypeBreakdown 
                  breakdown={conDashboard?.project_type_breakdown}
                  accentColor="blue"
                  testIdPrefix="con"
                  collapsed={kpiCollapse.con.breakdown}
                  onToggle={() => toggleKpi('con', 'breakdown')}
                />

                {/* CON Projects Table */}
                <Card className="bg-[#2A3240] border-white/10 overflow-hidden mt-4">
                  <CardContent className="p-0">
                    <ProjectTable 
                      projects={paginatedConProjects} 
                      testIdPrefix="con-project"
                      hoursData={hoursData}
                      showHoursColumn={hasAnyHoursData}
                      onDismissFlag={handleDismissArchiveFlag}
                      onMarkComplete={handleMarkComplete}
                      onCreateFolders={handleCreateFolders}
                      creatingFoldersId={creatingFoldersId}
                      canCreateFolders={isAdmin}
                    />
                    <div className="px-4 pb-3">
                      <Pagination 
                        currentPage={conPage} 
                        totalPages={conTotalPages} 
                        onPageChange={setConPage}
                        testIdPrefix="con"
                      />
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </div>
          )}
        </div>
      )}
    </div>
  );
}
